import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/assets";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        className="fixed left-0 right-0 top-3 z-50 px-3 sm:top-5 sm:px-6"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className={`mx-auto flex max-w-wide items-center justify-between rounded-full border px-5 py-3 transition-all duration-500 sm:px-6 ${
            scrolled
              ? "border-gold/20 bg-ink-900/75 shadow-xl shadow-ink-900/40 backdrop-blur-2xl"
              : "border-cream-50/10 bg-ink-900/30 backdrop-blur-xl"
          }`}
        >
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3"
          >
            <img
              src={BRAND.logo}
              alt="Zaya & Zari"
              className="h-10 w-10 object-contain sm:h-11 sm:w-11"
            />
            <span className="font-display text-xl tracking-[0.18em] text-cream-50 sm:text-2xl">
              ZAYA <span className="text-gold">&amp;</span> ZARI
            </span>
          </Link>

          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`link-underline text-sm uppercase tracking-[0.18em] transition-colors ${
                  location.pathname === l.to
                    ? "text-gold"
                    : "text-cream-50/80 hover:text-cream-50"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <Link
            to="/contact"
            data-testid="nav-book-btn"
            className="hidden rounded-full bg-gold px-6 py-3 text-xs uppercase tracking-[0.2em] text-ink-900 transition-colors hover:bg-antique md:inline-block"
          >
            Book a Visit
          </Link>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="text-cream-50 md:hidden"
            aria-label="Open menu"
          >
            <Menu size={28} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex flex-col bg-ink-900 px-8 py-8 md:hidden"
            initial={{ opacity: 0, clipPath: "circle(0% at 90% 8%)" }}
            animate={{ opacity: 1, clipPath: "circle(150% at 90% 8%)" }}
            exit={{ opacity: 0, clipPath: "circle(0% at 90% 8%)" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-xl tracking-[0.18em] text-gold">
                ZAYA &amp; ZARI
              </span>
              <button
                data-testid="nav-mobile-close"
                onClick={() => setOpen(false)}
                className="text-cream-50"
                aria-label="Close menu"
              >
                <X size={30} />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-7">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className="font-display text-5xl font-light text-cream-50"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <Link
              to="/contact"
              className="mt-auto rounded-full bg-gold px-6 py-4 text-center text-sm uppercase tracking-[0.2em] text-ink-900"
            >
              Book a Visit
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
